import { isDefined } from "../../infra3d";
import { Obj } from "../internal";
import { LRTransformer } from "./internal";
import { calculate_k_factor } from "./utils";
/**
 * Axis
 * @class Axis
 * @constructor
 */
export class Axis extends Obj {
    /**
     * Management of an axis. Provides methods for linear referencing based on one axis.
     * @method constructor
     * @param { type: string; features: any } featureCollection  The featureCollection (fc) representing the axis geometry.
     * @param {string=} nominallengthattribute The name of a property of the features in the fc, which represents a nominal length of the axis (Attribut mit der Soll-Laenge).
     * @param {string=} offsetattribute The name of a property of the features in th efc, which represents an u-offset of the axis.
     * @param {string[]} idattributearray An array of property names of the features in the fc, which represents a (connected) identifier of the axis.
     * @param {LRHandler=} parent
     */
    constructor(featureCollection, nominallengthattribute, offsetattribute, idattributearray, axisname, vtolerance = 100) {
        super();
        this.isnavigationref = true;
        this.name = axisname;
        this.axis = featureCollection;
        this.nominallengthattribute = nominallengthattribute;
        this.offsetattribute = offsetattribute;
        this.idattributearray = idattributearray;
        this.k = calculate_k_factor(featureCollection, nominallengthattribute, offsetattribute);
        this.lrtransformer = new LRTransformer(this.axis, this.k, vtolerance);
    }
    /**
     * Destroys the axis and the connected featurecollection.
     * @method destroy
     */
    destroy() {
        this.axis = null;
        this.nominallengthattribute = undefined;
        this.idattributearray = undefined;
        this.k = [];
        this.lrtransformer = null;
    }
    /**
     * Calculates uvw-Coordinates from xyz-Coordinates
     * @method xyz2uvw
     * @param {number} x
     * @param {number} y
     * @param {number} z
     * @param {Axis} navigationAxis The axis, where the u value should be calculated from.
     * @return {?Object}
     */
    xyz2uvw(x, y, z, navigationAxis) {
        if (!this.lrtransformer) {
            return null;
        }
        if (navigationAxis) {
            // Calculate the u-value from the navigation axis
            // and overwrite the u-value in the uvw object
            const uvw = this.lrtransformer.xyz2uvw(x, y, z);
            if (!uvw) {
                return null;
            }
            const nav_uvw = navigationAxis.xyz2uvw(uvw.axpoint[0], uvw.axpoint[1], uvw.axpoint[2]);
            if (!nav_uvw) {
                return null;
            }
            uvw.lrcoords[0] = nav_uvw.lrcoords[0];
            return uvw;
        }
        else {
            return this.lrtransformer.xyz2uvw(x, y, z);
        }
    }
    /**
     * Calculates xyz-Coordinates from uvw-Coordinates
     * @method uvw2xyz
     * @param {number} featureidx
     * @param {number} u
     * @param {number} v
     * @param {number} w
     * @return {Object}
     */
    uvw2xyz(featureidx, u, v, w) {
        if (!this.lrtransformer) {
            return null;
        }
        return this.lrtransformer.uvw2xyz(featureidx, u, v, w);
    }
    /**
     * Calculates the absolute u Value of all features in this.axis.
     * @method u2absU
     * @param {number} featureidx
     * @param {number} u
     * @return {number}
     */
    u2absU(featureidx, u) {
        return this.lrtransformer.u2absU(featureidx, u);
    }
    /**
     * Calculates the u Value from the absolute u Value. This will consider all features in this.axis.
     * @method absU2u
     * @param {number} absU
     * @return {number}
     */
    absU2u(absU) {
        return this.lrtransformer.absU2u(absU);
    }
    /**
     * Returns the Axis-Identifier
     * @method _getAxisIdentifier
     * @param {Number} featureidx
     * @return {Object}
     */
    getAxisIdentifier(featureidx) {
        const axisId = [];
        if (isDefined(this.idattributearray)) {
            for (let i = 0; i < this.idattributearray.length; i++) {
                axisId[this.idattributearray[i]] =
                    this.axis["features"][featureidx]["properties"][this.idattributearray[i]];
            }
        }
        else {
            axisId["featureidx"] = featureidx;
        }
        return axisId;
    }
}
//# sourceMappingURL=axis.js.map